import type { BuildingInStoreInfo, FloorInfo } from '@/services/dto/kiosk-config';
import type { GroupedPoiByBuildingId, GroupedPoiByFloorId, PoiItem } from '@/types/map';
import type { RoutePoint } from '@/types/route-simulation';
import type { IBuilding } from 'dabeeomaps/dist/src/model/map/IBuilding';
import type { IDataFloor } from 'dabeeomaps/dist/src/model/map/IDataFloor';
import { isValidNumber } from './number';

export type IDataFloorWithBuilding = IDataFloor & { buildingId: string };

const qrHereUrl = import.meta.env.VITE_QR_HERE_SERVICE_URL;

export const getGroupedPoiForBuilding = (
    groupedPoiByBuildingId: GroupedPoiByBuildingId,
    buildingId?: string,
    storeBuildings?: BuildingInStoreInfo[]
): GroupedPoiByFloorId => {
    if (!buildingId) {
        return new Map();
    }

    const directMatch = groupedPoiByBuildingId.get(buildingId);
    if (directMatch) {
        return directMatch;
    }

    const storeBuilding = storeBuildings?.find(
        (building) => building.studioBuildingId === buildingId || building.id === buildingId
    );
    if (storeBuilding) {
        const studioMatch = groupedPoiByBuildingId.get(storeBuilding.studioBuildingId);
        if (studioMatch) {
            return studioMatch;
        }
    }

    if (groupedPoiByBuildingId.size === 1) {
        return groupedPoiByBuildingId.values().next().value ?? new Map();
    }

    return new Map();
};

export const findNearestFloorWithPoi = (
    sortedFloors: FloorInfo[],
    name: string,
    groupedPoiByFloorId: GroupedPoiByFloorId,
    categoriesCode: string[]
): FloorInfo | null => {
    if (!Array.isArray(sortedFloors)) {
        return null;
    }
    const floorIdx = sortedFloors.findIndex((floor) => floor?.name['ko'] === name);

    if (floorIdx === -1) {
        return null;
    }

    const hasPoi = (name: string) => {
        const poiList = categoriesCode.flatMap((item) => {
            const poi = groupedPoiByFloorId.get(name)?.get(item);
            return poi ? poi : [];
        });
        return poiList && poiList.length > 0;
    };

    if (hasPoi(name)) {
        return sortedFloors.find((floor) => floor?.name['ko'] === name) ?? null;
    }

    let left = floorIdx - 1;
    let right = floorIdx + 1;

    while (left >= 0 || right < sortedFloors.length) {
        if (left >= 0 && hasPoi(sortedFloors[left]?.name['ko'])) {
            return sortedFloors[left];
        }
        if (right < sortedFloors.length && hasPoi(sortedFloors[right]?.name['ko'])) {
            return sortedFloors[right];
        }
        left--;
        right++;
    }

    return null;
};

export type NearestFloorWithBuilding = {
    floor: FloorInfo;
    buildingId: string;
};

const findFloorWithCategoryPoiInBuilding = (
    building: BuildingInStoreInfo,
    preferredFloorName: string,
    categoriesCode: string[],
    groupedPoiByBuildingId: GroupedPoiByBuildingId,
    storeBuildings?: BuildingInStoreInfo[]
): NearestFloorWithBuilding | null => {
    const buildingId = building.studioBuildingId;
    const sortedFloors = [...(building.floors ?? [])].sort((a, b) => a.order - b.order);
    if (!sortedFloors.length) {
        return null;
    }

    const groupedPoiByFloorId = getGroupedPoiForBuilding(groupedPoiByBuildingId, buildingId, storeBuildings);
    if (!groupedPoiByFloorId.size) {
        return null;
    }

    const hasPoi = (floorName: string) =>
        categoriesCode.some((categoryCode) => (groupedPoiByFloorId.get(floorName)?.get(categoryCode)?.length ?? 0) > 0);

    const withBuildingId = (floor: FloorInfo): NearestFloorWithBuilding => ({
        floor: { ...floor, buildingId: floor.buildingId || buildingId },
        buildingId,
    });

    if (preferredFloorName) {
        const nearestByPreferredFloor = findNearestFloorWithPoi(sortedFloors, preferredFloorName, groupedPoiByFloorId, categoriesCode);
        if (nearestByPreferredFloor) {
            return withBuildingId(nearestByPreferredFloor);
        }
    }

    for (const floor of sortedFloors) {
        const floorNameKo = floor?.name?.['ko'] || '';
        if (floorNameKo && hasPoi(floorNameKo)) {
            return withBuildingId(floor);
        }
    }

    return null;
};

export const findNearestFloorWithPoiIncludingAdjacentBuildings = (
    sortedFloors: FloorInfo[],
    floorName: string,
    groupedPoiByFloorId: GroupedPoiByFloorId,
    categoriesCode: string[],
    currentBuildingId: string,
    groupedPoiByBuildingId: GroupedPoiByBuildingId,
    storeBuildings?: BuildingInStoreInfo[]
): NearestFloorWithBuilding | null => {
    const nearestInCurrent = findNearestFloorWithPoi(sortedFloors, floorName, groupedPoiByFloorId, categoriesCode);
    if (nearestInCurrent) {
        return {
            floor: { ...nearestInCurrent, buildingId: nearestInCurrent.buildingId || currentBuildingId },
            buildingId: currentBuildingId,
        };
    }

    if (!storeBuildings?.length || !currentBuildingId) {
        return null;
    }

    const currentIdx = storeBuildings.findIndex(
        (building) => building.studioBuildingId === currentBuildingId || building.id === currentBuildingId
    );

    if (currentIdx === -1) {
        for (const building of storeBuildings) {
            if (building.studioBuildingId === currentBuildingId) {
                continue;
            }
            const result = findFloorWithCategoryPoiInBuilding(
                building,
                floorName,
                categoriesCode,
                groupedPoiByBuildingId,
                storeBuildings
            );
            if (result) {
                return result;
            }
        }
        return null;
    }

    let left = currentIdx - 1;
    let right = currentIdx + 1;

    while (left >= 0 || right < storeBuildings.length) {
        if (left >= 0) {
            const result = findFloorWithCategoryPoiInBuilding(
                storeBuildings[left],
                floorName,
                categoriesCode,
                groupedPoiByBuildingId,
                storeBuildings
            );
            if (result) {
                return result;
            }
            left--;
        }

        if (right < storeBuildings.length) {
            const result = findFloorWithCategoryPoiInBuilding(
                storeBuildings[right],
                floorName,
                categoriesCode,
                groupedPoiByBuildingId,
                storeBuildings
            );
            if (result) {
                return result;
            }
            right++;
        }
    }

    return null;
};

export const generateQrHereUrl = (serviceId: string, startPoint: RoutePoint, PoiIdEndPoint: string) => {
    if (!qrHereUrl || !startPoint?.position?.x || !startPoint?.position?.y || !startPoint?.floorId || !PoiIdEndPoint) {
        return '';
    }
    return `${qrHereUrl}/${serviceId}?${startPoint?.position?.x},${startPoint?.position?.y},FL-${startPoint.floorId},PO-${PoiIdEndPoint}`;
};

export const getAllFloorsFromIndoorBuildings = (buildings?: IBuilding[]): IDataFloorWithBuilding[] => {
    if (!Array.isArray(buildings) || !buildings?.length) {
        return [];
    }

    const indoorBuildings = buildings.filter((building) => building.buildingType === 'indoor');

    if (!indoorBuildings.length) {
        return [];
    }

    const allFloors: IDataFloorWithBuilding[] = [];
    indoorBuildings.forEach((building) => {
        building.floors.forEach((floor) => {
            allFloors.push({ ...floor, buildingId: building.id });
        });
    });

    return allFloors;
};

export const getUniqueFloorsByKoreanName = (floors: IDataFloor[]): IDataFloor[] => {
    if (!Array.isArray(floors) || !floors?.length) {
        return [];
    }

    const floorMap = new Map<string, number>();
    const uniqueFloors: IDataFloor[] = [];

    floors.forEach((floor) => {
        const koName = floor.name.find((n) => n.lang === 'ko');
        if (koName && !floorMap.has(koName.text)) {
            floorMap.set(koName.text, floor.order);
            uniqueFloors.push(floor);
        }
    });

    return uniqueFloors;
};

export const extractUniqueFloorsFromBuildings = (buildings?: IBuilding[]): IDataFloor[] => {
    const allFloors = getAllFloorsFromIndoorBuildings(buildings);
    return getUniqueFloorsByKoreanName(allFloors);
};

export const sortFloorsByOrder = (floors: IDataFloor[], direction: 'asc' | 'desc'): IDataFloor[] => {
    if (!Array.isArray(floors) || !floors?.length) {
        return [];
    }

    return [...floors].sort((a, b) => (direction === 'asc' ? a.order - b.order : b.order - a.order));
};

export const degreeToRadian = (degree?: number) => {
    return isValidNumber(degree) ? degree * (Math.PI / 180) : 0;
};

export const getDirectionVectors = (theta: number) => {
    const angle = -theta;

    const cosT = Math.cos(angle);
    const sinT = Math.sin(angle);

    const up = { x: sinT, y: -cosT };

    const right = { x: cosT, y: sinT };

    return {
        up,
        down: { x: -up.x, y: -up.y },
        left: { x: -right.x, y: -right.y },
        right,
    };
};

export const getPoiObjectIds = (poiList?: PoiItem[]): string[] => {
    if (!Array.isArray(poiList) || !poiList?.length) {
        return [];
    }

    const objectIds = poiList.map((item) => item?.objectId);
    return objectIds.filter((item) => item !== undefined);
};

/**
 * Helper function: Đợi map render xong trước khi thực hiện moveTo
 * Sử dụng requestAnimationFrame để đảm bảo map đã vẽ xong frame hiện tại
 * @param frames - Số frames cần đợi (mặc định là 3 frames để đảm bảo)
 */
export const waitForMapRender = async (frames: number = 3): Promise<void> => {
    return new Promise<void>((resolve) => {
        let frameCount = 0;

        const waitFrame = () => {
            frameCount++;
            if (frameCount >= frames) {
                resolve();
            } else {
                requestAnimationFrame(waitFrame);
            }
        };

        requestAnimationFrame(waitFrame);
    });
};

/**
 * Waits until the map dispatches `floor-changed` on the container element.
 * Falls back after timeout so floor changes still proceed if the event is missed.
 */
export const waitForFloorChangedEvent = async (container: HTMLElement, timeoutMs: number = 3000): Promise<void> => {
    return new Promise<void>((resolve) => {
        let settled = false;
        const finish = (): void => {
            if (settled) {
                return;
            }
            settled = true;
            container.removeEventListener('floor-changed', onFloorChanged);
            clearTimeout(timeoutId);
            void waitForMapRender(2).then(() => resolve());
        };
        const onFloorChanged = (): void => {
            finish();
        };
        const timeoutId = setTimeout(() => {
            finish();
        }, timeoutMs);
        container.addEventListener('floor-changed', onFloorChanged);
    });
};

/**
 * Changes floor and waits for `floor-changed`. Listener is attached before changeFloor
 * to avoid missing the event when the floor change completes synchronously.
 */
export const changeFloorAndWaitForEvent = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentMap: any,
    container: HTMLElement,
    floorId: string,
    timeoutMs: number = 3000
): Promise<void> => {
    const waitPromise = waitForFloorChangedEvent(container, timeoutMs);
    await currentMap?.context?.changeFloor?.(floorId);
    await waitPromise;
};

/**
 * Helper function nâng cao: Đợi và verify marker đã được vẽ trên map
 * Sử dụng hybrid approach: RAF + polling + timeout để đảm bảo marker thực sự được render
 * @param currentMap - DabeeoMap instance
 * @param markerId - ID của marker cần verify
 * @param position - Vị trí marker {x, y} để verify
 * @param maxRetries - Số lần retry tối đa (mặc định 10)
 * @param debugContext - Context để debug log
 */
export const waitForMarkerReady = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentMap: any,
    markerId?: string,
    _position?: { x?: number; y?: number },
    maxRetries: number = 10,
    debugContext?: string
): Promise<boolean> => {
    if (!currentMap || !markerId) {
        // eslint-disable-next-line no-console
        console.warn(`[${debugContext}] Invalid params for waitForMarkerReady`);
        return false;
    }

    // Tier 1: Đợi 3 frames trước (đảm bảo browser đã paint)
    await waitForMapRender(3);

    // Tier 2: Polling để verify marker thực sự tồn tại
    let retryCount = 0;
    const retryDelays = [50, 50, 100, 100, 150, 200, 250, 300]; // Progressive backoff

    while (retryCount < maxRetries) {
        try {
            // Kiểm tra marker có tồn tại không (nếu map API hỗ trợ)
            // Một số map engine có method getMarker hoặc markers.get
            const markerExists = currentMap?.markers?.get?.(markerId);

            if (markerExists) {
                if (debugContext) {
                    // eslint-disable-next-line no-console
                    console.log(`✅ [${debugContext}] Marker ready after ${retryCount} retries`);
                }
                return true;
            }

            // Nếu không có method get, chờ một thời gian hợp lý
            const delay = retryDelays[retryCount] || 300;
            await new Promise((resolve) => setTimeout(resolve, delay));

            retryCount++;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(`[${debugContext}] Error checking marker:`, error);
            retryCount++;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    }

    if (debugContext) {
        // eslint-disable-next-line no-console
        console.warn(`⚠️ [${debugContext}] Marker not verified after ${maxRetries} retries, proceeding anyway`);
    }

    // Return true anyway để không block flow, nhưng log warning
    return true;
};

/**
 * Helper function: Safe moveTo với retry logic
 * Đảm bảo moveTo được thực thi thành công, retry nếu fail
 * @param currentMap - DabeeoMap instance
 * @param position - Position to move to
 * @param floorId - Floor ID
 * @param transition - Enable transition animation
 * @param maxRetries - Số lần retry tối đa (mặc định 3)
 * @param debugContext - Context để debug
 */
export const safeMoveToMarker = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentMap: any,
    position: { x: number; y: number },
    floorId?: string,
    transition: boolean = true,
    maxRetries: number = 3,
    debugContext?: string
): Promise<boolean> => {
    if (!currentMap?.control?.moveTo) {
        // eslint-disable-next-line no-console
        console.warn(`[${debugContext}] Map control not available`);
        return false;
    }

    if (!isValidNumber(position?.x) || !isValidNumber(position?.y)) {
        // eslint-disable-next-line no-console
        console.warn(`[${debugContext}] Invalid position:`, position);
        return false;
    }

    let retryCount = 0;

    while (retryCount < maxRetries) {
        try {
            if (debugContext && retryCount > 0) {
                // eslint-disable-next-line no-console
                console.log(`[${debugContext}] Retry moveTo attempt ${retryCount + 1}/${maxRetries}`);
            }

            currentMap.control.moveTo({
                position: { x: position.x, y: position.y },
                floorId,
                transition,
            });

            // Đợi một frame để verify
            await waitForMapRender(1);

            // Get current camera position để verify moveTo đã work
            const currentCamera = currentMap?.control?.getCurrentCameraInfo?.();
            const cameraPosition = currentCamera?.position ?? (currentCamera?.center
                ? { x: currentCamera.center.x, y: currentCamera.center.z }
                : undefined);
            if (cameraPosition) {
                const distance = Math.sqrt(Math.pow(cameraPosition.x - position.x, 2) + Math.pow(cameraPosition.y - position.y, 2));

                // Nếu khoảng cách nhỏ hơn threshold, coi như thành công
                const threshold = 1000; // Điều chỉnh tùy map scale
                if (distance < threshold || retryCount === maxRetries - 1) {
                    if (debugContext) {
                        // eslint-disable-next-line no-console
                        console.log(`✅ [${debugContext}] MoveTo successful (distance: ${distance.toFixed(2)})`);
                    }
                    return true;
                }
            }

            // Nếu chưa thành công, đợi thêm
            await new Promise((resolve) => setTimeout(resolve, 100));
            retryCount++;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`[${debugContext}] MoveTo error:`, error);
            retryCount++;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
    }

    if (debugContext) {
        // eslint-disable-next-line no-console
        console.warn(`⚠️ [${debugContext}] MoveTo completed after ${maxRetries} attempts`);
    }

    return true;
};
